import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { type HealthRecord } from '../../services/lifeDataService';

interface Props {
  records: HealthRecord[];
  isZh: boolean;
}

interface ChartPoint {
  ts: string;          // label: "MM/DD HH:mm"
  tsSort: string;      // for sorting
  glucose?: number;    // 血糖
  systolic?: number;   // 收縮壓
  diastolic?: number;  // 舒張壓
  heartRate?: number;  // 心跳
}

const LINE_CONFIG = [
  { key: 'glucose',   color: '#ef5350', nameZh: '血糖',   nameEn: 'Glucose',   unit: 'mg/dL' },
  { key: 'systolic',  color: '#1976d2', nameZh: '收縮壓', nameEn: 'Systolic',  unit: 'mmHg' },
  { key: 'diastolic', color: '#42a5f5', nameZh: '舒張壓', nameEn: 'Diastolic', unit: 'mmHg' },
  { key: 'heartRate', color: '#e91e63', nameZh: '心跳',   nameEn: 'Heart Rate',unit: 'bpm'  },
] as const;

type LineKey = typeof LINE_CONFIG[number]['key'];

function buildPoints(records: HealthRecord[]): ChartPoint[] {
  const sorted = [...records].sort((a, b) => a.ts.localeCompare(b.ts));
  return sorted.reduce<ChartPoint[]>((acc, r) => {
    const label = r.ts.slice(5, 16).replace('T', ' '); // "MM-DD HH:mm"
    let pt = acc.find(p => p.tsSort === r.ts);
    if (!pt) {
      pt = { ts: label, tsSort: r.ts };
      acc.push(pt);
    }
    if (r.type === '血糖' && r.values[0] != null)  pt.glucose   = r.values[0];
    if (r.type === '血壓') {
      if (r.values[0] != null) pt.systolic  = r.values[0];
      if (r.values[1] != null) pt.diastolic = r.values[1];
      if (r.values[2] != null) pt.heartRate  = r.values[2];
    }
    return acc;
  }, []);
}

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; dataKey: string }>;
  label?: string;
  isZh: boolean;
}> = ({ active, payload, label, isZh }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.97)',
      border: '1px solid #e0e0e0',
      borderRadius: 10,
      padding: '10px 14px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      minWidth: 160,
    }}>
      <div style={{ fontSize: 11, color: '#757575', marginBottom: 6 }}>{label}</div>
      {payload.map(p => {
        const cfg = LINE_CONFIG.find(c => c.key === p.dataKey);
        const name = isZh ? (cfg?.nameZh ?? p.name) : (cfg?.nameEn ?? p.name);
        return (
          <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#37474f', flex: 1 }}>{name}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: p.color }}>
              {p.value} <span style={{ fontSize: 10, fontWeight: 400, color: '#9e9e9e' }}>{cfg?.unit}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

const HealthChart: React.FC<Props> = ({ records, isZh }) => {
  const points = useMemo(() => buildPoints(records), [records]);

  // 判斷哪些線有資料
  const hasData: Record<LineKey, boolean> = useMemo(() => ({
    glucose:   points.some(p => p.glucose   != null),
    systolic:  points.some(p => p.systolic  != null),
    diastolic: points.some(p => p.diastolic != null),
    heartRate: points.some(p => p.heartRate  != null),
  }), [points]);

  const anyData = Object.values(hasData).some(Boolean);

  if (!anyData) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#bdbdbd' }}>
        <Typography sx={{ fontSize: 14 }}>
          {isZh
            ? '尚無血糖或血壓紀錄可顯示圖表'
            : 'No glucose or blood pressure records to chart'}
        </Typography>
      </div>
    );
  }

  // 每條線獨立左/右 Y 軸
  // yAxisId="bp" for systolic/diastolic/heartRate (mmHg / bpm range ~40-200)
  // yAxisId="gl" for glucose (mg/dL range ~50-300)

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '24px 16px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <Typography sx={{ fontSize: 13, color: '#757575', mb: 2, px: 1 }}>
        {isZh ? '時序折線圖（僅顯示血糖・收縮壓・舒張壓・心跳）' : 'Time-series chart — Glucose, Systolic, Diastolic, Heart Rate'}
      </Typography>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={points} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="ts"
            tick={{ fontSize: 10, fill: '#9e9e9e' }}
            interval="preserveStartEnd"
          />
          {/* 左軸：血壓 / 心跳 */}
          <YAxis
            yAxisId="bp"
            orientation="left"
            tick={{ fontSize: 10, fill: '#9e9e9e' }}
            domain={['auto', 'auto']}
            label={{ value: 'mmHg / bpm', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#9e9e9e', dy: 50 }}
          />
          {/* 右軸：血糖 */}
          <YAxis
            yAxisId="gl"
            orientation="right"
            tick={{ fontSize: 10, fill: '#ef5350' }}
            domain={['auto', 'auto']}
            label={{ value: 'mg/dL', angle: 90, position: 'insideRight', fontSize: 10, fill: '#ef5350', dy: -30 }}
          />
          <Tooltip content={<CustomTooltip isZh={isZh} />} />
          <Legend
            formatter={(value: string) => {
              const cfg = LINE_CONFIG.find(c => c.key === value);
              return isZh ? (cfg?.nameZh ?? value) : (cfg?.nameEn ?? value);
            }}
            wrapperStyle={{ fontSize: 12 }}
          />
          {hasData.glucose && (
            <Line
              yAxisId="gl" type="monotone" dataKey="glucose"
              stroke="#ef5350" strokeWidth={2} dot={{ r: 3 }}
              connectNulls activeDot={{ r: 5 }}
            />
          )}
          {hasData.systolic && (
            <Line
              yAxisId="bp" type="monotone" dataKey="systolic"
              stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }}
              connectNulls activeDot={{ r: 5 }}
            />
          )}
          {hasData.diastolic && (
            <Line
              yAxisId="bp" type="monotone" dataKey="diastolic"
              stroke="#42a5f5" strokeWidth={2} dot={{ r: 3 }}
              connectNulls activeDot={{ r: 5 }}
            />
          )}
          {hasData.heartRate && (
            <Line
              yAxisId="bp" type="monotone" dataKey="heartRate"
              stroke="#e91e63" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 3"
              connectNulls activeDot={{ r: 5 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default HealthChart;
